import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../../hooks/useModalDrag';
import ModalHeader from '../../../../components/ui/ModalHeader';
import quoteDocumentsService from '../../../../services/quoteDocuments.service';
import type { QuoteDocumentRow } from '../../../../types/quoteDocuments';
import { saveBlob } from '../../../../utils/saveFile';

interface Props {
  doc: QuoteDocumentRow;
  quoteNumber: number;
  revisionNumber: number;
  onClose: () => void;
}

const IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tif', 'tiff', 'webp'];

/**
 * In-app document preview lightbox (mirrors the Pro Details PreviewLightbox):
 * fetches the blob via the authenticated service, shows PDFs in an <embed>,
 * images in an <img>, and offers a download link for anything else. Portaled to
 * <body> at z-[120] so it overlays the editor drawer. ESC / backdrop closes.
 */
const QuoteDocPreviewModal: React.FC<Props> = ({ doc, quoteNumber, revisionNumber, onClose }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Fetch through the service (Bearer token) then make an object URL — a direct
  // <embed src=/api/...> would skip auth and 401.
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setObjectUrl(null);
    setBlob(null);
    setError(null);
    quoteDocumentsService.previewBlob(quoteNumber, revisionNumber, doc.mapRecno)
      .then((fetched) => { if (cancelled) return; url = URL.createObjectURL(fetched); setObjectUrl(url); setBlob(fetched); })
      .catch((e) => { if (cancelled) return; setError(e?.message || 'Failed to load preview'); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [quoteNumber, revisionNumber, doc.mapRecno]);

  const ft = (doc.filetype || '').toLowerCase();
  const isPdf = ft === 'pdf';
  const isImage = IMAGE_TYPES.includes(ft);

  // Rendered only while open. Read-only doc preview: Alt+C closes.
  useAltHotkeys({ C: onClose }, undefined, { layer: true });

  // Rendered only while open (parent unmounts on close) — unmount resets the drag.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Document preview"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-scrim/60 p-6"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative max-w-[90vw] max-h-[90vh] rounded bg-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={doc.filename}
          onClose={onClose}
          closeLabel="Close preview"
          dragHandleProps={dragHandleProps}
          className="rounded-t"
        />
        <div className="p-2">
        {error ? (
          <div className="px-8 py-12 text-center text-red-600 dark:text-red-300">{error}</div>
        ) : !objectUrl ? (
          <div className="px-8 py-12 text-center text-muted" role="status">Loading preview…</div>
        ) : isPdf ? (
          <embed src={objectUrl} type="application/pdf" className="h-[78vh] w-[80vw]" />
        ) : isImage ? (
          <img src={objectUrl} alt={doc.filename} className="max-h-[80vh] max-w-full object-contain" />
        ) : (
          <button
            type="button"
            onClick={() => { if (blob) void saveBlob(blob, doc.filename); }}
            className="block w-full px-6 py-10 text-center text-blue-600 underline dark:text-blue-400"
          >
            Download {doc.filename}
          </button>
        )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default QuoteDocPreviewModal;
